import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { MUTUALITEITEN_INFO, REIMB_VERIFIED_DATE, Lang } from "../content/mutualiteiten";
import { pageTitle, lead, h2Rule, paragraph, ctaSx, fadeUp } from "../theme/marketingStyles";

/** /terugbetaling — uitgebreide uitleg met indicatieve bedragen per
 *  ziekenfonds (aanvullende verzekering, niet de RIZIV-conventie).
 *  Bedragen komen uit content/mutualiteiten.ts (sync met de API-seed);
 *  altijd met "geverifieerd op"-datum en verwijzing naar het eigen fonds. */
export default function Reimbursement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = ((i18n.language || "nl").slice(0, 2) as Lang);
  const verifiedDate = REIMB_VERIFIED_DATE[lang] || REIMB_VERIFIED_DATE.nl;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 6, sm: 9 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ textAlign: { xs: "center", md: "left" }, ...fadeUp(0) }}>
          <Typography variant="h1" sx={pageTitle}>{t("reimb.title")}</Typography>
        </Box>

        {/* Totaalprijs + gratis kennismaking (bestaande, gecontroleerde copy). */}
        <Typography sx={{ ...lead, mb: 2, ...fadeUp(80) }}>{t("reimb.p1")}</Typography>
        <Typography sx={{ ...paragraph, ...fadeUp(120) }}>{t("reimb.p4")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("reimb.howTitle")}</Typography>
        <Typography sx={paragraph}>{t("reimb.how1")}</Typography>
        <Typography sx={paragraph}>{t("reimb.how2")}</Typography>
        <Typography sx={paragraph}>{t("reimb.how3")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("reimb.tableTitle")}</Typography>
        <Typography sx={paragraph}>{t("reimb.tableIntro")}</Typography>
        {/* Zachte kaart i.p.v. omrande tabel; rijen gescheiden door fijne lijnen. */}
        <TableContainer sx={{ mb: 2, bgcolor: "var(--surface)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-md)" }}>
          <Table size="small" aria-label={t("reimb.tableTitle") as string}>
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "var(--surface-alt)", fontWeight: 700, border: 0, py: 1.5 } }}>
                <TableCell>{t("reimb.colFund")}</TableCell>
                <TableCell>{t("reimb.colAmount")}</TableCell>
                <TableCell>{t("reimb.colCap")}</TableCell>
                <TableCell>{t("reimb.colConditions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MUTUALITEITEN_INFO.map((m) => (
                <TableRow key={m.code} sx={{ "& td": { borderColor: "var(--border)", py: 1.5 }, "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top" }}>
                    {m.infoUrl ? (
                      <Link href={m.infoUrl} target="_blank" rel="noreferrer" sx={{ color: "inherit" }}>{m.name}</Link>
                    ) : (
                      m.name
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>{m.amount[lang] || m.amount.nl}</TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>{m.cap[lang] || m.cap.nl}</TableCell>
                  <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>{m.conditions[lang] || m.conditions.nl}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Verificatiedatum — dezelfde stille noot als op de homepagekaart. */}
        <Typography variant="caption" sx={{ display: "block", mb: 3, color: "text.secondary", lineHeight: 1.6 }}>
          {t("reimb.verifiedNote", { date: verifiedDate })}
        </Typography>
        <Typography sx={paragraph}>{t("reimb.otherFunds")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("reimb.attestTitle")}</Typography>
        <Typography sx={paragraph}>{t("reimb.p3")}</Typography>
        <Typography sx={paragraph}>{t("reimb.attest2")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("reimb.conventieTitle")}</Typography>
        <Typography sx={paragraph}>{t("reimb.p5")}</Typography>
        <Typography sx={paragraph}>{t("reimb.conventie2")}</Typography>

        <Box sx={{ mt: 7, textAlign: "center" }}>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>{t("reimb.ctaLead")}</Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/start")} sx={{ minWidth: 240, ...ctaSx }}>
            {t("reimb.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
